import React, { useState, useEffect } from 'react';
import './OrderSuccess.css';
import { Order, OrderItem, Stock, Product, Customer } from '../types';
import { formatOrderIdForDisplay, getDisplayAddress } from '../shared/orderUtils';
import { formatDiscountBreakdown } from '../shared/discount';
import { apiService } from '../services/api';

// Minimal product type for chips detection - only needs stockIds and options
interface ProductWithStocks {
    id: string;
    stockIds?: string[];
    options?: Array<{
        id: string;
        choices: Array<{
            id: string;
            stockIds?: string[];
        }>;
    }>;
}

// Large order threshold (more than 4 total items)
const LARGE_ORDER_THRESHOLD = 4;

const getTotalOrderQuantity = (order: Order): number => {
    return order.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
};

const isLargeOrder = (order: Order): boolean => {
    return getTotalOrderQuantity(order) > LARGE_ORDER_THRESHOLD;
};

const isSmallOrder = (order: Order): boolean => {
    return getTotalOrderQuantity(order) <= LARGE_ORDER_THRESHOLD;
};

/**
 * Check if any stock item with isChips=true is used in the order.
 * Works with order items that contain product references.
 */
const hasChipsInOrder = (order: Order, stocks: Stock[], products: ProductWithStocks[]): boolean => {
    if (!stocks || stocks.length === 0) return false;
    
    const chipsStockIds = new Set(
        stocks.filter(s => s.isChips === true).map(s => s.id)
    );
    
    if (chipsStockIds.size === 0) return false;
    
    // Build product lookup
    const productMap = new Map(products.map(p => [p.id, p]));
    
    for (const item of order.items) {
        const product = productMap.get(item.productId);
        if (!product) continue;
        
        // Check product-level stockIds
        if (Array.isArray(product.stockIds)) {
            for (const stockId of product.stockIds) {
                if (chipsStockIds.has(stockId)) return true;
            }
        }
        
        // Check option choice stockIds
        if (Array.isArray(item.selectedOptions) && Array.isArray(product.options)) {
            const optionMap = new Map(product.options.map(opt => [opt.id, opt]));
            
            for (const selection of item.selectedOptions) {
                const option = optionMap.get(selection.optionId);
                if (!option || !Array.isArray(selection.choiceIds)) continue;
                
                const choiceMap = new Map(option.choices.map(choice => [choice.id, choice]));
                
                for (const choiceId of selection.choiceIds) {
                    const choice = choiceMap.get(choiceId);
                    if (!choice || !Array.isArray(choice.stockIds)) continue;
                    
                    for (const stockId of choice.stockIds) {
                        if (chipsStockIds.has(stockId)) return true;
                    }
                }
            }
        }
    }
    
    return false;
};

/**
 * Check if order should show chips message (contains chips AND has 4 or fewer items)
 * Mutually exclusive with large order message.
 */
const isChipsOrder = (order: Order, stocks: Stock[], products: ProductWithStocks[]): boolean => {
    return isSmallOrder(order) && hasChipsInOrder(order, stocks, products);
};

interface OrderSuccessProps {
    className?: string;
    order?: Order | null;
    customer?: Customer | null;
    customerName?: string;
    customerAddress?: string;
    customerPhone?: string;
}

const OrderSuccess: React.FC<OrderSuccessProps> = ({
    className = '',
    order,
    customer,
    customerName,
    customerAddress,
    customerPhone
}) => {
    // Compute display address from customer data or fall back to legacy
    const displayAddress = customer 
        ? getDisplayAddress({
            address: customer.address || '',
            number: customer.number || '',
            street: customer.street || '',
            area: customer.area || '',
            postcode: customer.postcode || ''
          }, '')
        : customerAddress || '';
    // Fetch stocks and products for chips detection
    const [stocks, setStocks] = useState<Stock[]>([]);
    const [products, setProducts] = useState<Product[]>([]);

    useEffect(() => {
        // Only fetch if we have an order and it's a small order (chips message only applies to small orders)
        if (order && isSmallOrder(order)) {
            Promise.all([
                apiService.getStocks().catch(() => []),
                apiService.getProducts().catch(() => [])
            ]).then(([fetchedStocks, fetchedProducts]) => {
                setStocks(fetchedStocks);
                setProducts(fetchedProducts);
            });
        }
    }, [order]);

    if (!order) {
        // Fallback to simple success message if no order data
        return (
            <div className={`order-success ${className}`}>
                <div className="success-icon">✅</div>
                <h3>Order Placed Successfully!</h3>
                <p>Thanks for your order! It's now with us!</p>
            </div>
        );
    }

    const formatCurrency = (amount: number) => `£${amount.toFixed(2)}`;

    // Generate letter for item index (A, B, C, ... Z, AA, AB, ...)
    const getItemLetter = (index: number): string => {
        let result = '';
        let n = index;
        do {
            result = String.fromCharCode(65 + (n % 26)) + result;
            n = Math.floor(n / 26) - 1;
        } while (n >= 0);
        return result;
    };

    const showLetters = order.items.length > 1;

    const renderOrderItem = (item: OrderItem, index: number) => (
        <div key={index} className="success-order-item">
            <div className="item-main">
                {showLetters && (
                    <div className="item-letter">{getItemLetter(index)}</div>
                )}
                <div className="item-quantity">{item.quantity}×</div>
                <div className="item-details">
                    <div className="item-name">{item.name}</div>
                    {item.selectedOptions && item.selectedOptions.length > 0 && (
                        <div className="item-options">
                            {item.selectedOptions.map((option, optIndex) => {
                                // Use choiceNames if available, fallback to choiceIds
                                const choiceNames = (option as any).choiceNames;
                                const displayValue = choiceNames && choiceNames.length > 0
                                    ? choiceNames.join(', ')
                                    : option.choiceIds.join(', ');

                                return (
                                    <div key={optIndex} className="option-group">
                                        <span className="option-label">{option.optionName}:</span>
                                        <span className="option-value">{displayValue}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="item-price">{formatCurrency(item.price)}</div>
            </div>
        </div>
    );

    return (
        <div className={`order-success ${className}`}>
            <div className="follow-us-inline">
                <span className="follow-label">Follow Us</span>
                <a
                    href="https://www.facebook.com/DeliFeed"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="follow-btn facebook"
                    title="Follow us on Facebook"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                </a>
                <a
                    href="https://www.instagram.com/delifeed_cambourne"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="follow-btn instagram"
                    title="Follow us on Instagram"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                    </svg>
                </a>
            </div>

            <div className="success-header">
                <div className="success-icon">✅</div>
                <h3>Order Placed Successfully!</h3>
                <p className="order-reference">Order #{formatOrderIdForDisplay(order.id)}</p>
            </div>

            {/* Large Order Notice - mutually exclusive with Chips Order Notice */}
            {isLargeOrder(order) && (
                <div className="large-order-notice">
                    <span className="notice-icon">🎉</span>
                    <div>
                        <strong className="notice-title">Thanks so much for your order!</strong>
                        <p>Your order is larger than our average, so we just wanted to give you a quick heads-up. As a small local business, we prepare everything fresh, and for bigger orders, it may take a little longer than usual. We're doing our best to get it to you on time, and we truly appreciate your patience and understanding. Thanks again for supporting us! 💚</p>
                    </div>
                </div>
            )}

            {/* Chips Order Notice - only for small orders with chips, mutually exclusive with Large Order Notice */}
            {isChipsOrder(order, stocks, products) && (
                <div className="large-order-notice">
                    <span className="notice-icon">🎉</span>
                    <div>
                        <strong className="notice-title">Thanks for your order!</strong>
                        <p>Your order contains chips, and we just wanted to let you know that, as a local business, we use the healthiest method to cook them. This usually takes a little longer than the frying methods used by chain restaurants, so your order may take a bit longer than usual. As a Cambourne-based small business, we care deeply about your health and well-being. Thanks so much for your understanding! 💚</p>
                    </div>
                </div>
            )}

            <div className="success-content">
                {/* Customer Information */}
                <div className="success-section">
                    <h4>Delivery Details</h4>
                    <div className="customer-info">
                        {customerName && (
                            <p><strong>Name:</strong> {customerName}</p>
                        )}
                        {displayAddress && (
                            <p><strong>Address:</strong> {displayAddress}</p>
                        )}
                        {customerPhone && (
                            <p><strong>Phone:</strong> {customerPhone}</p>
                        )}
                        {/* Only show estimated delivery time for scheduled orders (not instant orders) */}
                        {order.scheduledSlot && !order.deliverNow && (
                            <p><strong>Estimated Delivery Time:</strong> {order.scheduledSlot}</p>
                        )}
                    </div>

                    {/* Slot bump warning - shown when delivery time was adjusted */}
                    {order.slotWasBumped && order.scheduledSlot && (
                        <div className="slot-bump-warning">
                            <span className="warning-icon">⏰</span>
                            <div>
                                <strong className="warning-title">Delivery Time Adjusted</strong>
                                {order.deliverNow ? (
                                    <p>The next delivery slot was full, so we've planned your order for {order.scheduledSlot}.</p>
                                ) : (
                                    <p>Your originally selected time ({order.originalRequestedSlot}) was no longer available, so we've scheduled your delivery for {order.scheduledSlot} instead.</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Call to change order */}
                    <div className="change-order-info">
                        <p>
                            If you'd like to make changes to your order,{' '}
                            <a 
                                href={`https://wa.me/447944488611?text=${encodeURIComponent(`From ${customerName || 'Customer'}, Address: ${displayAddress || 'Not provided'}. My update:`)}`}
                                className="whatsapp-link"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                message us on WhatsApp
                            </a>
                            {' '}or call us on{' '}
                            <a href="tel:+447944488611" className="phone-link">079 444 88 611</a>
                        </p>
                    </div>
                </div>

                {/* Order Items */}
                <div className="success-section">
                    <h4>Your Order ({getTotalOrderQuantity(order)} {getTotalOrderQuantity(order) === 1 ? 'item' : 'items'})</h4>
                    <div className="success-order-items">
                        {order.items.map((item, index) => renderOrderItem(item, index))}
                    </div>
                </div>

                {/* Order Totals */}
                <div className="success-section">
                    <div className="success-totals">
                        <div className="total-row">
                            <span>Subtotal:</span>
                            <span>{formatCurrency(order.subtotal)}</span>
                        </div>
                        {order.discountAmount && order.discountAmount > 0 && (
                            <div className="total-row discount-row">
                                <span>
                                    Discount {order.discountCode && `(${order.discountCode})`}
                                    {(() => {
                                        const breakdown = formatDiscountBreakdown(order.discountFixedAmount, order.discountPercentage);
                                        return breakdown ? ` - ${breakdown}` : '';
                                    })()}
                                    :
                                </span>
                                <span>−{formatCurrency(order.discountAmount)}</span>
                            </div>
                        )}
                        {order.driverTipAmount && order.driverTipAmount > 0 && (
                            <div className="total-row">
                                <span>Driver Tip:</span>
                                <span>{formatCurrency(order.driverTipAmount)}</span>
                            </div>
                        )}
                        <div className="total-row grand-total">
                            <span>Total:</span>
                            <span>{formatCurrency(order.total)}</span>
                        </div>
                    </div>
                </div>

                <p className="success-message">
                    Thanks for your order! It's now with us and we'll deliver it {order.deliverNow ? 'as soon as possible' : 'at your scheduled time'}.
                </p>

                <div className="allergen-warning">
                    <span className="warning-icon">⚠️</span>
                    <div>
                        <strong className="warning-title">Allergen Warning</strong>
                        <p>We unfortunately cannot guarantee allergen-free products. If anyone who will consume this order has food allergies, please contact us immediately to cancel your order.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderSuccess;